<template>
  <div v-if="traitsFenced" class="py-8" data-testid="traits-fenced-hint">
    {{ t('Reviews.agency.view.traits.notActive') }}
  </div>
  <div v-if="traits.length > 0" class="py-6">
    <div class="pb-12">
      <div class="mb-1 flex items-center justify-between text-headline-xs">
        <span data-testid="usp-title">{{ t('Reviews.agency.view.traits.title') }}</span>
        <OpenAgencyProfile :slug="agency.slug" />
      </div>
      <div class="text-body-s" data-testid="usp-description">{{ t('Reviews.agency.view.traits.description') }}</div>
    </div>
    <div class="grid grid-cols-2 gap-x-6">
      <div v-for="lang in ALLOWED_LOCALES" :key="lang">
        <div>
          <ChipBadge class="mb-4 uppercase" rounded>{{ lang.toUpperCase() }}</ChipBadge>
          <MessageBanner
            v-if="hasEmptyEnTranslation && lang === 'en'"
            class="my-4"
            data-testid="empty-translation-banner"
            :headline="t('Reviews.agency.view.traits.messageBanner.headline')"
            :message="t('Reviews.agency.view.traits.messageBanner.message')"
            :type="'error'"
          />
          <TraitItem
            v-for="(trait, index) in traits"
            :key="index"
            :trait="trait"
            :language="lang"
            fallback-language="de"
            :data-testid="`usp-item-${lang}`"
          />
        </div>
      </div>
    </div>
  </div>
  <ReviewsEmptyView
    v-else
    class="py-8"
    data-testid="no-traits-view"
    :title="t('Reviews.agency.view.traits.noTraitsTitle')"
    :description="t('Reviews.agency.view.traits.noTraitsInfo')"
    :action="{
      label: t('Reviews.agency.view.actions.addTraits'),
      to: {
        name: ReviewsRouteName.ReviewsAgencyEditTraits,
        params: { agencyId: agency.id },
      },
    }"
  >
    <template #image>
      <NoTraitsIcon />
    </template>
  </ReviewsEmptyView>
</template>
<script setup lang="ts">
import { ChipBadge, MessageBanner } from '@ramp106/omrjs-core-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import ReviewsEmptyView from '@/components/ReviewsEmptyView.vue'
import type { AgencyData } from '@/gql/reviews'
import { ALLOWED_LOCALES, mapTranslationsArrToObj } from '@/helpers/translationsHelper'
import { ReviewsRouteName } from '@/router/types.ts'

import OpenAgencyProfile from '../OpenAgencyProfile.vue'
import NoTraitsIcon from './NoTraitsIcon.vue'
import TraitItem from './TraitItem.vue'

const props = defineProps<{
  agency: AgencyData
}>()

const traitsFenced = computed(() => !props.agency.configFlags.traitsEditable)

const traits = computed(() => {
  return props.agency.traits.map((trait) => {
    return {
      id: trait.id,
      position: trait.position,
      kind: trait.kind,
      title: trait.titleTranslations ? mapTranslationsArrToObj(trait.titleTranslations) : undefined,
      description: trait.descriptionTranslations ? mapTranslationsArrToObj(trait.descriptionTranslations) : undefined,
    }
  })
})

const hasEmptyEnTranslation = computed(() => {
  return traits.value.some((trait) => trait.title?.en === '' || trait.description?.en === '')
})

const { t } = useI18n()
</script>
